import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { columns, createData, generateButton, statusLabel } from "../utils/homePageLogic";
import { routes } from "../routes/routes";
import TableComponent from "../components/Table";
import { useStore } from "../store/useStore";
import { useUserStore } from "../store/useUserStore";
import useFetchParticipantApplications from "../hooks/useFetchParticipantApplications";
import { formatBogotaDate } from "../utils/dateUtils";
import useGetUser from "../hooks/useGetUser";
import useAssignappUpdateStatus from "../hooks/useAssignappUpdateStatus";
import useSendTimer from "../hooks/useSendTimer";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import ErrorModal from "../components/Modal/ErrorModal";
import CircularProgress from "@mui/material/CircularProgress";
import useFetchAssignapp from "../hooks/useFetchAssignapp";
import WarningModal from "../components/Modal/WarningModal";
import { useAuthStore } from "../store/useAuthStore";

function HomePage() {
  const navigate = useNavigate();
  const {
    setTimerVisible,
    setAssignappId,
    setIsModalInstructionsOpen,
    setApplicationStartTime,
    setApplicacionData,
    setCloseApplication,
    setShowCloseApplicationModal,
    setUserHasClosedApplicationModal,
  } = useStore();

  // Autenticación
  const sessionUser = useAuthStore((s) => s.session?.user);
  const logout = useAuthStore((s) => s.logout);

  const { getUser, data: userData, error: userError, loading: userLoading } = useGetUser();
  const { data, loading, error: applicationsError, refetch } = useFetchParticipantApplications(userData?.id);

  const { updateAssignappStatus, error: updateStatusError } = useAssignappUpdateStatus();
  const { updateUserInfo } = useUserStore();
  const { sendTimer, error: sendTimerError } = useSendTimer();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const noFoundUserError = userError?.error?.includes("No se encontró el usuario con email");
  const noFoundErrorMessage = `${userError?.error} en la base de datos, por favor contacte a la persona encargada.`;
  const { fetchAssignapp, error: fetchAssignappError } = useFetchAssignapp();
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  // Maneja el cierre de la modal cuando la aplicación fue cerrada por un gestor
  const handleRedirectModal = () => {
    setShowBlockedModal(false);
    refetch();
  };

  useEffect(() => {
    if ((userError || applicationsError || updateStatusError || sendTimerError || fetchAssignappError) && !noFoundUserError) {
      const error = userError || applicationsError || updateStatusError || sendTimerError || fetchAssignappError;
      setErrorMessage(error?.message);
      setShowError(true);
    }
  }, [userError, applicationsError, updateStatusError, sendTimerError, noFoundUserError, fetchAssignappError]);

  useEffect(() => {
    if (sessionUser?.email) {
      getUser({ email: sessionUser.email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUser?.email]);

  useEffect(() => {
    if (userData) {
      updateUserInfo(userData);
    }
  }, [userData, updateUserInfo]);

  const handleButtonClick = async (id, assignappId) => {
    const assignapp = await fetchAssignapp(assignappId);
    if (!assignapp) return;

    if (assignapp.status === "complete") {
      setShowBlockedModal(true);
      return;
    }

    const { name, instructions, time_warning_minutes, time_close_warning_minutes, time_auto_close_minutes, timed } = assignapp.aplicacione;
    const isTimed = timed ?? true;
    const currentPhase = assignapp.current_phase;
    const inAppTimeExpired = assignapp.in_app_time_expired === true;
    const isReactivated = assignapp.reactivated === true;
    const wasPending = assignapp.status === "pending";

    setAssignappId(assignappId);

    if (currentPhase === "in_app" && inAppTimeExpired && !isReactivated) {
      setShowBlockedModal(true);
      return;
    }

    if (wasPending) {
      await updateAssignappStatus({
        assignapp_id: assignappId,
        status: "incomplete",
      });
    }

    if (currentPhase === "pre") {
      setTimerVisible(false);
      setIsModalInstructionsOpen(false);
      setApplicationStartTime(null);
      navigate(`${routes.cuestionariosPorFase}?id=${id}&phase=pre`, { replace: true });
      return;
    }

    if (currentPhase === "post") {
      setTimerVisible(false);
      setIsModalInstructionsOpen(false);
      setApplicationStartTime(null);
      navigate(`${routes.cuestionariosPorFase}?id=${id}&phase=post`, { replace: true });
      return;
    }

    setApplicacionData({
      name,
      instructions,
      timed: isTimed,
      time_warning_minutes,
      time_close_warning_minutes,
      time_auto_close_minutes,
      reactivated: isReactivated,
    });

    if (wasPending) {
      if (isTimed) {
        const now = Date.now();
        const initialEpoc = Math.floor(now / 1000);

        await sendTimer({
          startTime: initialEpoc,
          assignappId,
        });

        setApplicationStartTime(initialEpoc);
      } else {
        setApplicationStartTime(null);
      }
    }

    setTimerVisible(true);
    setCloseApplication(false);
    setShowCloseApplicationModal(false);
    setUserHasClosedApplicationModal(false);
    setIsModalInstructionsOpen(true);

    navigate(`${routes.aplicacion}?id=${id}`, { replace: true });
  };

  //No cuenta como “en progreso” aquellas que su ventana de tiempo no se encuentra activa
  const now = new Date();
  const hasIncomplete =
    Array.isArray(data) &&
    data.some((item) => {
      const start = new Date(item.activation_window_start);
      const end = new Date(item.activation_window_end);
      return item.status === "incomplete" && now >= start && now <= end;
    });

  const rows =
    (data || [])?.map((item) => {
      const name = item.name;
      const appId = item?.id?.toString();
      const currentAssignappId = item?.assignapp_id?.toString();
      const maxDate = formatBogotaDate(item.date_max);
      const status = statusLabel(item.status);
      const instructions = item?.instructions;

      // Valida únicamente si aún no ha iniciado la ventana de disponibilidad
      const now = new Date();
      const windowStart = new Date(item.activation_window_start);
      const isBeforeWindow = now < windowStart;

      const disabledButtons = isBeforeWindow || item.status === "complete";

      const windowStartFormatted = formatBogotaDate(item.activation_window_start);

      const labelOverride = isBeforeWindow ? "No disponible" : null;

      const tooltip = isBeforeWindow ? `Disponible desde el ${windowStartFormatted}` : null;

      const actions = generateButton(
        item.status,
        appId,
        currentAssignappId,
        handleButtonClick,
        disabledButtons,
        name,
        instructions,
        labelOverride,
        tooltip,
      );

      return createData(name, status, maxDate, actions);
    }) || [];

  useEffect(() => {
    if (loading || !Array.isArray(data)) return;

    // Si no hay ninguna aplicación en progreso se apaga el timer
    if (!hasIncomplete) {
      setTimerVisible(false);
      setApplicationStartTime(null);
    }
  }, [loading, data, hasIncomplete, setTimerVisible, setApplicationStartTime]);

  return (
    <div className="w-full max-w-6xl mx-auto min-h-[calc(100dvh-15rem)] flex items-center">
      <div className="w-full xl:flex xl:items-start xl:gap-16">
        {/* Información del usuario */}
        <div className="w-full min-w-0 xl:w-1/3 xl:flex-none">
          {!userLoading && !userError && (
            <>
              <h1 className="mb-4 break-words">
                Bienvenido/a, {userData?.name} {userData?.last_name}
              </h1>

              <p className="text-lg font-medium break-words">{userData?.email}</p>
            </>
          )}
        </div>

        {/* Tabla */}
        <div className="mt-8 w-full min-w-0 xl:mt-0 xl:flex-1">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <CircularProgress />
            </div>
          )}

          <div className="w-full min-w-0 overflow-x-auto">
            <TableComponent columns={columns} rows={rows} sortBy="date" asc={false} prioritizeButtons={true} noData={!data?.length} />
          </div>
        </div>

        <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="¡Error!" message={errorMessage} />

        <ErrorModal openModal={!!noFoundUserError} message={noFoundErrorMessage} handleClick={logout} buttonText="Cerrar sesión" />

        <WarningModal
          open={showBlockedModal}
          onClose={handleRedirectModal}
          onConfirm={handleRedirectModal}
          title="Aplicación cerrada por un gestor"
          description="Un gestor ha cerrado esta aplicación. No puedes continuar con ella."
        />
      </div>
    </div>
  );
}

export default HomePage;
